import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsBuilding, BsGeoAlt, BsPeopleFill } from 'react-icons/bs';

import Checkbox from '#globalComponents/Checkbox/Checkbox';
import Panel from '#globalComponents/Overlay/Panel';
import Popover from '#globalComponents/Overlay/Popover';
import { RangeSliderWithInput } from '#leadOverviewFeature/components/LeadOverviewPage/LeadCriteriaModal/criteriaFormAtoms';

import { COMPANY_LIMIT, companiesLabel, contactModeLabel } from '../../constants/conversation';

import Chip from './Chip';
import ContactsPopover from './ContactsPopover';

/**
 * The persistent, cost-relevant settings of the composer: companies, contacts, Google Maps.
 * Every change is handed up at once; the server persists it as the mandate.
 */
const LimitChips = ({ value, onChange, disabled }) => {
	const [open, setOpen] = useState(null);
	const toggle = (key) => setOpen((current) => (current === key ? null : key));

	return (
		<div className={`flex flex-wrap items-center gap-1.5 ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
			<Popover
				open={open === 'companies'}
				onClose={() => setOpen(null)}
				size="s"
				trigger={
					<Chip
						icon={BsBuilding}
						label={companiesLabel(value.companyLimit)}
						active={open === 'companies'}
						onClick={() => toggle('companies')}
						title="Maximale Anzahl Firmen"
					/>
				}
			>
				<Panel.Body>
					<RangeSliderWithInput
						min={COMPANY_LIMIT.min}
						max={COMPANY_LIMIT.max}
						value={value.companyLimit}
						onChange={(companyLimit) => onChange({ companyLimit })}
						label="Max. Firmen"
					/>
					<p className="mt-2 text-xs text-bgBase-800">
						Obergrenze für die Suche: ein Firmen-Credit je neu übernommener Firma, der Assistent stoppt,
						sobald sie erreicht ist.
					</p>
				</Panel.Body>
			</Popover>

			<Popover
				open={open === 'contacts'}
				onClose={() => setOpen(null)}
				size="m"
				trigger={
					<Chip
						icon={BsPeopleFill}
						label={contactModeLabel(value.contactMode, 'short')}
						active={open === 'contacts'}
						onClick={() => toggle('contacts')}
						title="Welche Kontakte je Firma"
					/>
				}
			>
				<ContactsPopover value={value} onChange={onChange} />
			</Popover>

			<Chip
				icon={BsGeoAlt}
				label="Google Maps"
				on={value.mapsEnabled}
				onClick={() => onChange({ mapsEnabled: !value.mapsEnabled, mapsNationwide: false })}
				title={
					value.mapsEnabled
						? 'Google Maps ist aktiv: alle gefundenen Firmen werden übernommen und abgerechnet. Klicken zum Deaktivieren.'
						: 'Google Maps aus: nur der Firmenpool. Klicken zum Aktivieren.'
				}
			/>
			{value.mapsEnabled && (
				<div className="basis-full rounded-card border border-caution-500/40 bg-caution-200 px-3 py-2 text-xs text-caution-700">
					<p>
						<b>
							Alle gefundenen Google-Maps-Firmen werden in deine Liste übernommen und mit je einem Credit
							abgerechnet, auch weniger passende.
						</b>{' '}
						Keine Vorschau möglich; die Firmen-Obergrenze gilt für Pool und Google Maps zusammen.
					</p>
					<div className="mt-2">
						<Checkbox
							id="agent-maps-nationwide"
							checked={value.mapsNationwide}
							onChange={(mapsNationwide) => onChange({ mapsNationwide })}
							label={<span className="text-xs">Deutschlandweit suchen erlauben</span>}
						/>
						<p className="mt-1 pl-6 text-xs text-caution-700">
							Ohne diese Option bleibt die externe Suche auf Ort oder Umkreis des Auftrags begrenzt.
							Deutschlandweit kann die Obergrenze deutlich schneller erreichen.
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

LimitChips.propTypes = {
	value: PropTypes.shape({
		companyLimit: PropTypes.number.isRequired,
		contactMode: PropTypes.string.isRequired,
		roles: PropTypes.arrayOf(PropTypes.string).isRequired,
		phone: PropTypes.bool.isRequired,
		maxPerCompany: PropTypes.number.isRequired,
		mapsEnabled: PropTypes.bool.isRequired,
		mapsNationwide: PropTypes.bool.isRequired,
	}).isRequired,
	onChange: PropTypes.func.isRequired,
	disabled: PropTypes.bool,
};

export default LimitChips;
